/**
 * Preferences API — narrow client over GET/PUT /v1/preferences/:child_id.
 *
 * Phase 1C.1 endpoints (deployed; verified by 8 vitest unit tests):
 *  - GET  /v1/preferences/:child_id  materialized view (auto-defaults)
 *  - PUT  /v1/preferences/:child_id  partial update (.strict, ≥1 field)
 *
 * Used by:
 *  - Summary screen (Phase 2 task #7) to render the prefs recap.
 *  - Parent Zone (Phase 5) for direct edits.
 *
 * The materialized read includes auto-defaults from readOrInitPreferences
 * (daily frequency, balanced difficulty, dailyCount=3, empty arrays).
 * PUT applies strict — at least one field required per zod schema.
 */

import { apiFetch } from './client';
import type {
  ClassId,
  DifficultyPref,
  FrequencyPref,
  UserPreferences,
  DailyCount,
} from '@funpaper/shared';

type GetPreferencesResponse = {
  ok: true;
  preferences: UserPreferences;
};

export async function getPreferences(childId: number): Promise<UserPreferences> {
  const response = await apiFetch<GetPreferencesResponse>(
    `/v1/preferences/${childId}`,
  );
  return response.preferences;
}

export type UpdatePreferencesInput = {
  preferredTema?: string[];
  preferredActivity?: string[];
  dailyCount?: DailyCount;
  difficulty?: DifficultyPref;
  frequency?: FrequencyPref;
  goals?: string[];
  commitmentPactSigned?: boolean;
  /** Plan 21: kelompok usia preferensi (null = ikuti kelas profil anak). */
  preferredClassId?: ClassId | null;
};

type UpdatePreferencesResponse = {
  ok: true;
  preferences: UserPreferences;
};

export async function updatePreferences(
  childId: number,
  input: UpdatePreferencesInput,
): Promise<UserPreferences> {
  const response = await apiFetch<UpdatePreferencesResponse>(
    `/v1/preferences/${childId}`,
    {
      method: 'PUT',
      body: input,
    },
  );
  return response.preferences;
}
